import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { omit } from 'lodash';
// utils
import axios from '../../utils/axios';
import { KanbanCard, KanbanColumn } from '../../@type/kanban';
import { dispatch } from '../store';
import { getErrorMessage } from '@/utils/stringHelper';
// ----------------------------------------------------------------------

type DeleteColumnPayload = {
  columnId: string;
};

type PersistColumnPayload = string[];

type AddTaskPayload = { card: KanbanCard; columnId: string };

type DeleteTaskPayload = { cardId: string; columnId: string };

type PersistCardPayload = Record<string, KanbanColumn>;

function objFromArray<Type extends Record<string, unknown>>(array: Type[], key: keyof Type = 'id' as keyof Type): Record<string, Type> {
  return array.reduce<Record<string, Type>>((accumulator, current) => {
    accumulator[String(current[key])] = current;
    return accumulator;
  }, {});
}

type KanbanBoardPayload = {
  cards: KanbanCard[];
  columns: KanbanColumn[];
  columnOrder: string[];
};

type InitialState = {
  isLoading: boolean;
  error: boolean;
  board: {
    cards: Record<string, KanbanCard>;
    columns: Record<string, KanbanColumn>;
    columnOrder: string[];
  };
};

const initialState: InitialState = {
  isLoading: false,
  error: false,
  board: {
    cards: {},
    columns: {},
    columnOrder: []
  }
};

const slice = createSlice({
  name: 'kanban',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state: InitialState) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state: InitialState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = Boolean(action.payload);
    },

    // GET BOARD
    getBoardSuccess(state: InitialState, action: PayloadAction<KanbanBoardPayload>) {
      state.isLoading = false;
      const board = action.payload;
      const cards = objFromArray<KanbanCard>(board.cards);
      const columns = objFromArray<KanbanColumn>(board.columns);
      const { columnOrder } = board;
      state.board = {
        cards,
        columns,
        columnOrder
      };
    },

    // CREATE NEW COLUMN
    createColumnSuccess(state: InitialState, action: PayloadAction<KanbanColumn>) {
      const newColumn = action.payload;
      state.isLoading = false;
      state.board.columns = {
        ...state.board.columns,
        [newColumn.id]: newColumn
      };
      state.board.columnOrder.push(newColumn.id);
    },

    persistCard(state: InitialState, action: PayloadAction<PersistCardPayload>) {
      const columns = action.payload;
      state.board.columns = columns;
    },

    persistColumn(state: InitialState, action: PayloadAction<PersistColumnPayload>) {
      state.board.columnOrder = action.payload;
    },

    addTask(state: InitialState, action: PayloadAction<AddTaskPayload>) {
      const { card, columnId } = action.payload;
      if (!state.board.columns[columnId]) {
        return;
      }
      if (!card.id) { return; }
      state.board.cards[card.id] = card;
      state.board.columns[columnId].cardIds.push(card.id);
    },

    deleteTask(state: InitialState, action: PayloadAction<DeleteTaskPayload>) {
      const { cardId, columnId } = action.payload;

      state.board.columns[columnId].cardIds = state.board.columns[columnId].cardIds.filter(
        (id: string) => id !== cardId
      );
      state.board.cards = omit(state.board.cards, [cardId]);
    },

    // UPDATE COLUMN
    updateColumnSuccess(state: InitialState, action: PayloadAction<KanbanColumn>) {
      const column = action.payload;

      state.isLoading = false;
      state.board.columns[column.id] = column;
    },

    // DELETE COLUMN
    deleteColumnSuccess(state, action: PayloadAction<DeleteColumnPayload>) {
      const { columnId } = action.payload;
      const deletedColumn = state.board.columns[columnId];

      state.isLoading = false;
      state.board.columns = omit(state.board.columns, [columnId]);
      state.board.cards = omit(state.board.cards, [...deletedColumn.cardIds]);
      state.board.columnOrder = state.board.columnOrder.filter((c: string) => c !== columnId);
    }
  }
});

// Reducer
export default slice.reducer;

export const { actions } = slice;

// ----------------------------------------------------------------------

export function getBoard(): () => Promise<void> {
  return async (): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/kanban/board');
      dispatch(slice.actions.getBoardSuccess(response.data.board));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(slice.actions.hasError(errorMessage));
    }
  };
}

// ----------------------------------------------------------------------

export function createColumn(newColumn: { name: string }): () => Promise<void> {
  return async (): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/kanban/columns/new', newColumn);
      dispatch(slice.actions.createColumnSuccess(response.data.column));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(slice.actions.hasError(errorMessage));
    }
  };
}

// ----------------------------------------------------------------------

export function updateColumn(columnId: string, updateColumn: KanbanColumn): () => Promise<void> {
  return async (): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/api/kanban/columns/update', {
        columnId,
        updateColumn
      });
      dispatch(slice.actions.updateColumnSuccess(response.data.column));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(slice.actions.hasError(errorMessage));
    }
  };
}

// ----------------------------------------------------------------------

export function deleteColumn(columnId: string): () => Promise<void> {
  return async (): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.post('/api/kanban/columns/delete', { columnId });
      dispatch(slice.actions.deleteColumnSuccess({ columnId }));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(slice.actions.hasError(errorMessage));
    }
  };
}

// ----------------------------------------------------------------------

export function persistColumn(newColumnOrder: string[]): () => void {
  return (): void => {
    dispatch(slice.actions.persistColumn(newColumnOrder));
  };
}

// ----------------------------------------------------------------------

export function persistCard(columns: Record<string, KanbanColumn>): () => void {
  return (): void => {
    dispatch(slice.actions.persistCard(columns));
  };
}

// ----------------------------------------------------------------------

export function addTask({ card, columnId }: { card: KanbanCard; columnId: string }): () => void {
  return (): void => {
    dispatch(slice.actions.addTask({ card, columnId }));
  };
}

// ----------------------------------------------------------------------

export function deleteTask({ cardId, columnId }: { cardId: string; columnId: string }): () => void {
  return (): void => {
    dispatch(slice.actions.deleteTask({ cardId, columnId }));
  };
}
